import { ROUTE_TO } from 'redux-router-kit';
import { QueryClient } from 'react-query';

import { Profile } from '@zapier/shared-entities';
import { readCookie } from '@zapier/cookies';
import { fetchJson } from '@zapier/toolbox-browser-fetch';

import { selectAppId } from 'app/developer-v3/store/selectors';
import * as CliApp from 'app/entities/CliApp';
import { getQueryKeyOfPartnerDashboard } from 'app/developer-v3/hooks';
import { queryOptions as dashboardQueryOptions } from 'app/developer-v3/pages/partnerProgram/constants';
import { ZAPIER_APP_BASE_URL } from 'app/common/constants';

const intercomAPIBase = 'https://api-iam.intercom.io';
const intercomAppID = 'eg4np976';
const queryClient = new QueryClient();

async function fetchPartnerDashboard(
  serviceId: number
): Promise<{
  appHealthScore: string | undefined;
  appTier: string | undefined;
}> {
  const defaultReturn = { appHealthScore: undefined, appTier: undefined };

  if (!serviceId) {
    console.error('Failed to load Partner Dashboard information for Intercom');
    return defaultReturn;
  }

  try {
    const url = new URL(
      `/api/v3/partner/${serviceId}/dashboard`,
      ZAPIER_APP_BASE_URL
    );
    const data = await queryClient.fetchQuery({
      queryKey: getQueryKeyOfPartnerDashboard({ serviceId }),
      queryFn: () => fetchJson(url.href),
      staleTime: dashboardQueryOptions.staleTime,
    });

    return {
      appHealthScore: data?.health?.current_score,
      appTier: data?.status?.tier,
    };
  } catch (error) {
    console.error(error.message);
    return defaultReturn;
  }
}

interface Account {
  id: number;
  name: string;
}

async function fetchCompany(
  currentAccountId: ID
): Promise<Account | undefined> {
  const url = new URL(
    `/api/v4/accounts/${currentAccountId}/`,
    ZAPIER_APP_BASE_URL
  );
  return await queryClient.fetchQuery({
    queryKey: url.href,
    async queryFn({ queryKey: [key] }) {
      const resp = await fetch(key, { credentials: 'include' });
      if (!resp.ok) {
        console.error(resp.statusText);
        return undefined;
      }
      const { id, name } = await resp.json();
      return { id, name };
    },
    staleTime: Infinity,
  });
}

// Intercom should be initialized with the `boot` command and updated with the
// `update` command. This let's us keep track of if we've already booted, so we
// can send the correct command.
let intercomMethod: 'boot' | 'update' = 'boot';

async function updateIntercom(store: any): Promise<void> {
  const state = store.getState();
  const currentProfile = Profile.selectors.all.currentProfile(state);
  // User level
  const userId = Profile.selectors.id(currentProfile);
  const email = Profile.selectors.email(currentProfile);
  const firstName = Profile.selectors.firstName(currentProfile);
  const lastName = Profile.selectors.lastName(currentProfile);
  const role = Profile.selectors.roles(currentProfile)?.[0]?.role;
  const accessLevel = role;
  const platformUserRole = role;
  // Company level
  const appId = selectAppId(state);
  const app = CliApp.selectors.all.entity(appId, state);
  const appIntegrationId = CliApp.selectors.id(app);
  const serviceId = CliApp.selectors.serviceId(app);
  const appName = CliApp.selectors.title(app) || CliApp.selectors.key(app);
  const appStatus = CliApp.selectors.status(app);
  const { appTier, appHealthScore } = await fetchPartnerDashboard(serviceId);
  const currentAccountId = readCookie('currentAccountId');
  const company = await fetchCompany(currentAccountId);
  // The `ROUTE_TO` action is dispatched before the `window.location` has
  // actually changed. That's a problem because Intercom doesn't take any
  // parameters, it just gets the page from `window.location` when it's called.
  // It doesn't seem that any actions are dispatched to indicate that the
  // `ROUTE_TO` is complete. To get around this, we can use `setTimeout(..., 0)`
  // to put the Intercom call at the end of the JS event queue. This way the
  // `ROUTE_TO` action will have been processed and `window.location` will have
  // been updated by the time Intercom is actually invoked.
  setTimeout(() => {
    window.Intercom(intercomMethod, {
      api_base: intercomAPIBase,
      app_id: intercomAppID,
      user_id: userId,
      email,
      first_name: firstName,
      last_name: lastName,
      access_level: accessLevel,
      platform_user_role: platformUserRole,
      company: {
        id: company?.id,
        name: company?.name,
        app_integration_id: appIntegrationId && Number(appIntegrationId),
        app_name: appName,
        app_status: appStatus,
        app_tier: appTier,
        app_health_score: appHealthScore,
      },
    });
    intercomMethod = 'update';
  }, 0);
}

const intercomMiddleware = store => next => action => {
  // Intercom is loaded by index.html, so it's not available in tests
  if (!window.Intercom) {
    return next(action);
  }

  // When routing to a new URL
  if (action && action.type === ROUTE_TO) {
    updateIntercom(store);
  }

  // When a profile fetch is completed
  if (
    action &&
    action.type === 'entity/FETCH_COLLECTION_DONE' &&
    action.entityType === 'partnerProfile'
  ) {
    updateIntercom(store);
  }

  // When a CLI app fetch is completed
  if (
    action &&
    action.type === 'entity/FETCH_ENTITY_DONE' &&
    action.entityType === 'cliApp'
  ) {
    updateIntercom(store);
  }

  return next(action);
};

export default intercomMiddleware;
